<script lang="ts">
  import { screens, playerTruth, version, glossary } from '../store';
  import Die from './dice-tray/die.svelte';
  import { createDie, DieMaterial } from './models/die.type';
  import type { Die as DieType } from './models/die.type';
  import {
    createPlayerInstance,
    PlayerClass,
    type PlayerArchetype,
    type PlayerComplexity,
  } from './models/player.type';
  import Portrait from './components/portrait.svelte';
  import Carousel from './components/carousel.svelte';
  import Difficulty from './components/difficulty.svelte';
  import { send, receive } from '../animation';
  import { type PlayerDefinition, type Player, addItem } from './models/player.type';
  import Icon from './icon/icon.svelte';
  import { openOptions } from './modals/modals';
  import { createBattle } from './models/battle.type';
  import { createCampaign } from './utils/map';
  import { createCampaign as createBanditCampaign } from './utils/campaign-bandit';
  import { createPrologue } from './utils/prologue';
  import Text from './components/text.svelte';
  import { Items } from './models/item.type';
  import { track } from './utils/playerTracking';
  import { activateTutorial } from './utils/tutorial';
  import Chrome from './components/chrome.svelte';
  import Frame from './components/frame.svelte';
  import { EnemyType } from './models/enemy.type';
  import { playAudio, SoundType } from './components/audio.svelte';
  import { Screen } from './models/screen.type';

  // Raw: these hold players, and a proxy of a player is a copy that drifts
  // away from the real one.
  let selected: any = $state.raw();
  let dice: Array<Array<DieType>> = $state([]);
  let step = $state('class');

  let classes: Array<Player> = [
    createPlayerInstance(PlayerClass.Ranger),
    createPlayerInstance(PlayerClass.Fighter),
    createPlayerInstance(PlayerClass.Rogue),
    createPlayerInstance(PlayerClass.Elementalist),
  ];
  let archetypes: Array<Player> = $state.raw([]);
  let complexities: Array<PlayerComplexity> = $state.raw([]);

  // The card each carousel has in its centre.
  const firstClass = 1;
  let classOffset = $state(firstClass);
  let archetypeOffset = $state(1);

  /* function openGlossary(){
    glossary.open.set(true);
  } */

  const select = (player: any) => {
    selected = player;

    const root: HTMLElement | null = document.querySelector(':root');
    root?.style.setProperty('--player-colour', selected.colour || null);
  };

  select(classes[firstClass]);

  const selectClass = () => {
    step = 'archetype';

    archetypes = selected.archetypes.map((archetype: PlayerArchetype) => {
      const player = createPlayerInstance(selected.type, archetype.id);
      player.title = archetype.title;
      player.text = archetype.text;

      return player;
    });
    complexities = selected.archetypes.map((archetype: PlayerArchetype) => archetype.complexity);

    archetypeOffset = Math.min(1, archetypes.length - 1);
    select(archetypes[archetypeOffset]);
  };

  const selectArchetype = () => {
    step = 'summary';
    var index = archetypeOffset;

    dice[0] = [createDie(selected.dice1[index], 1, 'p' + 1)];
    dice[1] = [createDie(selected.dice2[index], 2, 'p' + 2)];
    dice[2] = [createDie(selected.dice3[index], 3, 'p' + 3)];
  };

  const goDice = () => {
    step = 'dice';
  };
  const selectDice = () => {
    step = 'summary';
  };

  const prevDie = (index: number) => {
    var pos = selected['dice' + (index + 1)].indexOf(dice[index][0].type) - 1;

    if (pos < 0) {
      pos = selected['dice' + (index + 1)].length - 1;
    }

    dice[index] = [createDie(selected['dice' + (index + 1)][pos], 1, 'p' + Math.random())];
  };
  const nextDie = (index: number) => {
    var pos = selected['dice' + (index + 1)].indexOf(dice[index][0].type) + 1;

    if (pos >= selected['dice' + (index + 1)].length) {
      pos = 0;
    }

    dice[index] = [createDie(selected['dice' + (index + 1)][pos], 1, 'p' + Math.random())];
  };

  const viewTechniques = () => {
    glossary.open.set(true);
    glossary.selected.set('techniques');
    glossary.searchDie.set(dice.map((die) => die[0]));
  };

  function createCharacter() {
    $playerTruth = createPlayerInstance(selected.type, selected.archetype);
    if ($playerTruth) {
      $playerTruth.dice = [];
      dice.forEach((die, index) => {
        $playerTruth.dice[index * 2] = {
          type: die[0].type,
          level: 0,
          material: DieMaterial.Normal,
        };
        $playerTruth.dice[index * 2 + 1] = {
          type: die[0].type,
          level: 0,
          material: DieMaterial.Normal,
        };
      });
    }
  }

  function startPrologue() {
    createCharacter();
    playAudio(SoundType.BattleStart);

    let map = createCampaign();
    let prologueMap = createPrologue();

    screens.set(Screen.Map, { map });
    screens.push(Screen.Map, { map: prologueMap });
    screens.push(Screen.Event, { event: prologueMap.nodes[0].event });

    track('Create Character', {
      class: selected.type,
      archetype: selected.archetype,
      dice: dice.map((die) => die[0].label).join(','),
      starting: 'prologue',
    });

    activateTutorial();
  }

  function startPractice() {
    createCharacter();
    playAudio(SoundType.BattleStart);

    screens.push(Screen.BattleStart, {
      battle: createBattle(EnemyType.TrainingDummy),
    });
  }

  function startCampaign() {
    playAudio(SoundType.BattleStart);
    createCharacter();
    addItem($playerTruth, Items.PotionHealth);

    let map = createCampaign();

    screens.set(Screen.Map, { map: map });

    track('Create Character', {
      class: selected.type,
      archetype: selected.archetype,
      dice: dice.map((die) => die[0].label).join(','),
      starting: 'campaign',
    });
  }

  function back() {
    switch (step) {
      case 'class':
        screens.set(Screen.Menu);
        break;
      case 'archetype':
        step = 'class';
        select(classes[classOffset]);
        break;
      case 'dice':
        step = 'summary';
        break;
      case 'summary':
        step = 'archetype';
        break;
    }
  }
</script>

<div class="background"></div>
<div class="container {step == 'class' ? 'visible' : 'hidden'}">
  <Frame>
    <p class="step-title">Choose a Class</p>
    <div class="picker">
      <Carousel
        items={classes}
        bind:offset={classOffset}
        change={select}
        active={step == 'class'}
      />
      <div class="descriptions">
        {#each classes as player, index}
          <div class="description" class:current={index === classOffset}>
            <h1>{player.title}</h1>
            <p class="flavour">{player.text}</p>
          </div>
        {/each}
      </div>
    </div>
    <div class="button-container">
      <Chrome click={back} chroming="obsidian" edge="circle" sound={true}>
        <div class="button">Back</div>
      </Chrome>

      <Chrome click={selectClass} chroming="gold" edge="circle" sound={true}>
        <div class="button">Select</div>
      </Chrome>
    </div>
  </Frame>
</div>

<div class="container {step == 'archetype' ? 'visible' : 'hidden'}">
  <Frame>
    <p class="step-title">Choose an Archetype</p>
    <div class="picker">
      <Carousel
        items={archetypes}
        bind:offset={archetypeOffset}
        change={select}
        active={step == 'archetype'}
      />
      <div class="descriptions">
        {#each archetypes as player, index}
          {@const trait = player.traits[0]}
          <div class="description" class:current={index === archetypeOffset}>
            <h1>{player.title}</h1>
            <Difficulty level={complexities[index]} />
            <p class="flavour">{player.text}</p>

            {#if trait}
              <div class="trait">
                {#if trait.icon}
                  <div class="trait-icon">
                    <Icon name={trait.icon} />
                  </div>
                {/if}
                <div>
                  <h2>{trait.title}</h2>
                  <Text class="text" data={trait.text} />
                </div>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    </div>
    <div class="button-container">
      <Chrome click={back} chroming="obsidian" edge="circle" sound={true}>
        <div class="button">Back</div>
      </Chrome>

      <Chrome click={selectArchetype} chroming="gold" edge="circle" sound={true}>
        <div class="button">Select</div>
      </Chrome>
    </div>
  </Frame>
</div>

<div class="container {step == 'dice' ? 'visible' : 'hidden'}">
  <Frame>
    <h1>Choose your Equipment</h1>

    <div class="description">
      {#if dice.length}
        <div class="die-list">
          <div class="die-title">
            <h3>Weapon:</h3>
            <h3>{dice[0][0].type}</h3>
          </div>
          <div class="die-scroll">
            <Chrome
              chroming="silver"
              edge="circle"
              thickness="thin"
              click={() => {
                prevDie(0);
              }}
            >
              <Icon name="prevarrow" width="30px" height="30px" />
            </Chrome>
            <div class="die-container-outer">
              {#each dice[0] as die, index (die.id)}
                <div class="die-container" in:receive={{ key: die.id }} out:send={{ key: die.id }}>
                  <Die {die} die3D={false} hover={true} />
                </div>
              {/each}
            </div>
            <Chrome
              chroming="silver"
              edge="circle"
              thickness="thin"
              click={() => {
                nextDie(0);
              }}
            >
              <Icon name="nextarrow" width="30px" height="30px" />
            </Chrome>
          </div>

          <div class="die-title">
            <h3>Item:</h3>
            <h3>{dice[1][0].type}</h3>
          </div>
          <div class="die-scroll">
            <Chrome
              chroming="silver"
              edge="circle"
              thickness="thin"
              click={() => {
                prevDie(1);
              }}
            >
              <Icon name="prevarrow" width="30px" height="30px" />
            </Chrome>
            <div class="die-container-outer">
              {#each dice[1] as die, index (die.id)}
                <div class="die-container" in:receive={{ key: die.id }} out:send={{ key: die.id }}>
                  <Die die={dice[1][0]} die3D={false} hover={true} />
                </div>
              {/each}
            </div>
            <Chrome
              chroming="silver"
              edge="circle"
              thickness="thin"
              click={() => {
                nextDie(1);
              }}
            >
              <Icon name="nextarrow" width="30px" height="30px" />
            </Chrome>
          </div>

          <div class="die-title">
            <h3>Ability:</h3>
            <h3>{dice[2][0].type}</h3>
          </div>
          <div class="die-scroll">
            <Chrome
              chroming="silver"
              edge="circle"
              thickness="thin"
              click={() => {
                prevDie(2);
              }}
            >
              <Icon name="prevarrow" width="30px" height="30px" />
            </Chrome>
            <div class="die-container-outer">
              {#each dice[2] as die, index (die.id)}
                <div class="die-container" in:receive={{ key: die.id }} out:send={{ key: die.id }}>
                  <Die die={dice[2][0]} die3D={false} hover={true} />
                </div>
              {/each}
            </div>
            <Chrome
              chroming="silver"
              edge="circle"
              thickness="thin"
              click={() => {
                nextDie(2);
              }}
            >
              <Icon name="nextarrow" width="30px" height="30px" />
            </Chrome>
          </div>
        </div>
      {/if}

      <Chrome chroming="obsidian" edge="circle" click={viewTechniques} sound={true}>
        <div class="button">View Techniques</div>
      </Chrome>
    </div>

    <div class="button-container">
      <Chrome chroming="gold" edge="circle" click={selectDice} sound={true}>
        <div class="button">Select</div>
      </Chrome>
    </div>
  </Frame>
</div>

<div class="container summary-step {step == 'summary' ? 'visible' : 'hidden'}">
  <Frame>
    <div class="summary-layout">
      <div class="summary">
        <Portrait data={selected} />
        {#if step == 'summary'}
          <div class="dice">
            <div class="die-container">
              <Die die={dice[0][0]} die3D={false} hover={true} />
            </div>
            <div class="die-container">
              <Die die={dice[1][0]} die3D={false} hover={true} />
            </div>
            <div class="die-container">
              <Die die={dice[2][0]} die3D={false} hover={true} />
            </div>
            <!-- <button on:click={goDice}>Customise</button> -->
          </div>
        {/if}
      </div>

      <div class="buttons">
        <Chrome click={startPrologue} chroming="obsidian" edge="circle">
          <div class="button">Prologue/Tutorial</div>
        </Chrome>
        <Chrome click={startPractice} chroming="obsidian" edge="circle">
          <div class="button">Practice</div>
        </Chrome>
        <Chrome click={startCampaign} chroming="gold" edge="circle">
          <div class="button">Campaign</div>
        </Chrome>
        <Chrome click={back} chroming="obsidian" edge="circle" sound={true}>
          <div class="button">Back</div>
        </Chrome>

        <!-- <button class='btn' on:click={startPrologue}>Prologue/Tutorial</button>
      <button class='btn' on:click={startPractice}>Practice</button>
      <button class='btn' on:click={startCampaign}>Campaign</button>
      <button class='btn' on:click={back}>Back</button> -->
      </div>
    </div>
  </Frame>
</div>

<style lang="less">
  @import '../defines.less';

  // Landscape phones: too short to stack a carousel over its description.
  @short: ~'(orientation: landscape) and (max-height: 560px)';

  .background {
    background-image: url('../assets/backgrounds/camp.webp');
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    bottom: 0;
  }

  .container {
    // Sized from the viewport directly. The app-wide breakpoints fold in a
    // max-height clause, which hands a 1366x768 laptop the phone portrait.
    --column-size: clamp(100px, min(30vw, 30vh), 300px);

    // Side by side, the text column is what runs out of room, so give the
    // frame the width the stacked layout leaves unused.
    @media @short {
      // Less the 12px gutter each side and the frame's own padding and border.
      --width: calc(100vw - 80px);
    }

    display: grid;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    padding: 12px;
    // A last resort for a screen too short for the frame: scroll rather than
    // leave the buttons out of reach. The frame's own auto margins centre it,
    // and unlike align-items: center they never push its top out of reach.
    overflow-y: auto;
    background-position: center;
    background-size: cover;
    text-shadow: 2px 2px black;
    text-align: center;
    transition: opacity 0.5s;

    position: absolute;
    max-height: unset;
    max-width: unset;

    h1 {
      font-size: clamp(1.4em, min(7vw, 6vh), 3.2em);

      @media @short {
        font-size: clamp(1.2em, 5vh, 2em);
      }
    }
    h2 {
      font-size: clamp(1.1em, min(4.5vw, 3.5vh), 1.5em);
    }
  }

  // Which step this is. Kept quiet, so the name of what is being chosen is
  // the one big line on the screen.
  .step-title {
    margin: 0;
    font-size: clamp(0.95em, min(3.6vw, 2.4vh), 1.3em);
    letter-spacing: 0.18em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .picker {
    display: grid;
    grid-gap: clamp(8px, 2vh, 20px);
    margin: clamp(8px, 2vh, 20px) 0;

    @media @short {
      grid-template-columns: auto 1fr;
      align-items: center;
      text-align: left;

      .description {
        align-items: flex-start;
      }
    }
  }

  // Every card's description is laid into the same cell, so the block is
  // always as tall as the longest one and the frame holds still while
  // browsing. Only the current one is shown.
  .descriptions {
    display: grid;
  }

  .description {
    grid-area: 1 / 1;
    display: flex;
    flex-direction: column;
    grid-gap: clamp(6px, 1.5vh, 16px);
    align-items: center;
    visibility: hidden;

    @media @short {
      grid-gap: 4px;
    }
    opacity: 0;
    transition:
      opacity 0.3s,
      visibility 0.3s;

    &.current {
      visibility: visible;
      opacity: 1;
    }

    h1 {
      line-height: 1.1;
    }
  }

  .flavour,
  .trait {
    max-width: 55ch;
  }

  .flavour,
  .trait :global(.text) {
    font-size: clamp(14px, 1.9vh, 17px);
    line-height: 1.45;

    @media @short {
      font-size: 13px;
      line-height: 1.35;
    }
  }

  // The one piece of rules text on the screen, set apart from the flavour and
  // led by the same icon the card wears in its corner.
  .trait {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-gap: 12px;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding: 10px 14px;
    text-align: left;
    background: fade(@panel-sunken, 85%);
    border: 1px solid @panel-line;
    border-radius: 6px;
    box-shadow: inset 0 0 12px fade(@black, 60%);

    // Every line counts on a landscape phone; the card already wears the icon.
    @media @short {
      grid-template-columns: 1fr;
      padding: 6px 10px;

      .trait-icon {
        display: none;
      }
    }

    h2 {
      margin-bottom: 2px;
      font-size: clamp(1em, 2.4vh, 1.2em);
    }

    .trait-icon {
      width: clamp(28px, 4.5vh, 40px);
      height: clamp(28px, 4.5vh, 40px);
      padding: 4px;
      border-radius: 4px;
      background: fade(@black, 50%);
    }
  }

  .die-list {
    display: flex;
    flex-direction: column;
    grid-gap: 10px;

    .die-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 80%;
      margin: auto;
      align-items: center;

      h3 {
        margin-bottom: 0;
        margin-top: 10px;
        text-transform: capitalize;
      }
    }

    .die-container-outer {
      position: relative;
      width: var(--dice-size);
      height: var(--dice-size);

      .die-container {
        position: absolute;
      }
    }

    .die-scroll {
      display: grid;
      grid-gap: 10px;
      grid-template-columns: 60px var(--dice-size) 60px;

      & :global(.content svg) {
        padding: 5px;
        fill: #8f8f8f;
      }
      & :global(div:nth-child(1) .content svg) {
        padding-left: 2px;
      }
      & :global(div:nth-child(3) .content svg) {
        padding-right: 2px;
      }
    }
  }

  .summary-step {
    --width: min(500px, calc(90vw - 80px));
    // The three dice stand beside the portrait, so share its height out.
    --dice-size: clamp(36px, calc((var(--column-size) - 20px) / 3), 80px);

    @media @short {
      --width: min(720px, calc(90vw - 40px));
    }
  }

  .summary-layout {
    display: grid;
    grid-gap: clamp(8px, 2vh, 20px);

    @media @short {
      grid-template-columns: auto 1fr;
      align-items: center;
    }
  }

  .summary {
    display: flex;
    grid-gap: 20px;
    align-items: center;
    justify-content: center;

    .dice {
      display: flex;
      flex-direction: column;
      grid-gap: 10px;
      align-items: center;
    }
  }

  .button-container {
    display: flex;
    justify-content: end;
    width: 100%;
    grid-gap: 20px;
  }

  .buttons {
    display: grid;
    grid-gap: clamp(8px, 1.5vh, 20px);
    align-items: center;
    border-radius: 20px;
    max-width: 400px;
    margin: auto;
    width: 100%;
  }

  .button {
    font-size: clamp(18px, 4vh, 28px);
    padding: 6px 12px;
    color: #ffffd5;
  }
</style>
